import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Calendar, Wallet } from "lucide-react";

const options = [
  {
    icon: Calendar,
    label: "Location",
    main: "170€",
    sub: "/ mois",
    desc: "Vivez avec votre handpan, sans engagement à long terme.",
    accent: "#8C9A86",
  },
  {
    icon: Wallet,
    label: "Achat 4× sans frais",
    main: "270€",
    sub: "× 4 mois",
    desc: "Étalez l'investissement, gardez l'instrument à vie.",
    accent: "#A8843F",
  },
  {
    icon: Sparkles,
    label: "Achat comptant",
    main: "1 083€",
    sub: "tarif boutique",
    desc: "Le D-Kurd Minor à son meilleur prix, livré ou retrait.",
    accent: "#C86B52",
    highlight: true,
  },
];

export default function Rental() {
  return (
    <section
      id="location"
      className="relative py-28 md:py-40 px-6 md:px-12 lg:px-20 bg-sand overflow-hidden"
      data-testid="rental-section"
    >
      {/* Ambient blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 right-10 w-[400px] h-[400px] rounded-full bg-terracotta/10 blur-[100px]" />
        <div className="absolute bottom-10 left-10 w-[400px] h-[400px] rounded-full bg-sage/15 blur-[100px]" />
      </div>

      <div className="relative max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-16"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-[1px] bg-terracotta" />
            <span className="text-xs uppercase tracking-[0.3em] text-ink/60">
              Trois façons d'y accéder
            </span>
          </div>
          <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light leading-[0.95] text-ink">
            Adoptez le <span className="italic-serif text-terracotta">handpan</span> à votre rythme.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-ink/75 font-light leading-relaxed">
            Location mensuelle, paiement en plusieurs fois ou achat direct —
            choisissez la formule qui s'accorde avec votre pratique. Le D-Kurd
            Minor n'attend que vos mains.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {options.map((o, i) => {
            const Icon = o.icon;
            return (
              <motion.div
                key={o.label}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{
                  duration: 0.8,
                  delay: i * 0.12,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className={`relative rounded-3xl p-8 md:p-10 transition-all duration-500 hover:-translate-y-2 ${
                  o.highlight
                    ? "bg-walnut text-cream md:scale-105"
                    : "bg-cream text-ink border border-ink/8 hover:border-terracotta/40"
                }`}
                data-testid={`rental-option-${i}`}
              >
                {o.highlight && (
                  <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-terracotta text-cream text-[10px] uppercase tracking-[0.2em] font-medium">
                    Recommandé
                  </div>
                )}
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-6"
                  style={{
                    backgroundColor: o.highlight
                      ? "rgba(247,245,240,0.1)"
                      : `${o.accent}15`,
                  }}
                >
                  <Icon size={20} style={{ color: o.accent }} />
                </div>
                <div
                  className={`text-xs uppercase tracking-[0.2em] mb-4 ${
                    o.highlight ? "text-cream/60" : "text-ink/55"
                  }`}
                >
                  {o.label}
                </div>
                <div className="flex items-baseline gap-2 mb-4">
                  <span
                    className="font-serif text-5xl md:text-6xl font-light"
                    style={{ color: o.accent }}
                  >
                    {o.main}
                  </span>
                  <span
                    className={`text-sm ${
                      o.highlight ? "text-cream/60" : "text-ink/55"
                    }`}
                  >
                    {o.sub}
                  </span>
                </div>
                <p
                  className={`text-sm leading-relaxed ${
                    o.highlight ? "text-cream/75" : "text-ink/70"
                  }`}
                >
                  {o.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Smart deduction note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-10 flex items-start gap-4 p-6 md:p-8 rounded-2xl bg-cream border border-terracotta/20"
          data-testid="rental-deduction-note"
        >
          <div className="shrink-0 w-10 h-10 rounded-full bg-terracotta/15 flex items-center justify-center">
            <Sparkles size={16} className="text-terracotta" />
          </div>
          <div className="flex-1">
            <h4 className="font-serif text-xl text-ink mb-1">
              Vous hésitez ? Louez d'abord, achetez après.
            </h4>
            <p className="text-sm md:text-base text-ink/70 font-light leading-relaxed">
              Les mensualités de location sont{" "}
              <span className="font-medium text-terracotta">
                déductibles du prix d'achat
              </span>{" "}
              si vous décidez de garder l'instrument. Aucun risque, juste le
              temps qu'il vous faut pour tomber amoureux.
            </p>
          </div>
        </motion.div>

        <div className="mt-12 flex flex-wrap gap-4">
          <a
            href="tel:+33767396699"
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-ink text-cream text-sm uppercase tracking-[0.15em] hover:bg-terracotta transition-all duration-500 hover:scale-105"
            data-testid="rental-cta-call"
          >
            Réserver une location
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="mailto:contact@soundyoga.fr"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-ink/20 text-ink text-sm uppercase tracking-[0.15em] hover:border-terracotta hover:text-terracotta transition-all duration-500"
            data-testid="rental-cta-email"
          >
            Poser une question
          </a>
        </div>
      </div>
    </section>
  );
}
